/**
 * Resolution state for the Kanban board's cards read (SE-7624).
 *
 * `useKanbanBoardQueries` used to read only `data` and `loading` off
 * `useListKanbanCardsQuery` — `error` was destructured nowhere, only
 * `console.warn`'d inside the query's `onError`. Because
 * `providers/ApolloClient.tsx` sets `defaultOptions.watchQuery.errorPolicy:
 * "all"`, a failed read RESOLVES with an `error` on the hook result rather
 * than rejecting, so the failure was fully observable and simply never read.
 * The result: a failed cards fetch produced `data === undefined` +
 * `loading === false` — pixel-identical to a board that genuinely has no
 * players — and the board rendered empty columns, telling the user a fact
 * about their board ("nothing here") that the read never established.
 *
 * These helpers turn the raw hook result into a discriminated union so every
 * outcome is a distinct, exhaustively-handled state, following the shape
 * SE-7580 landed for the Similar Players tab
 * (`utils/similarPlayersQueryState.ts`) and SE-7569 refined for
 * `usePlanStatus`. They are pure so the mapping is testable without
 * rendering, and so the "which state wins" precedence is stated once instead
 * of re-derived per call site.
 * @module features/player-kanban/utils/kanbanCardsState
 */

import type { KanbanCard } from "@/generated/graphql";

/**
 * Resolution state of the Kanban cards read.
 *
 * - `loading` — no answer yet: the first fetch, or the cards query is
 *   skipped for want of a board id (an absent result is not evidence of zero
 *   cards).
 * - `empty` — the read SUCCEEDED and the board genuinely has no cards. This
 *   is the only state permitted to render "this board has no players".
 * - `failed` — the read errored (for a reason other than a permission
 *   denial) and there is nothing trustworthy to show. Renders an explicit
 *   error with retry, never an empty board.
 * - `denied` (SE-8534) — the read was REFUSED because the viewer holds no
 *   grant for this board (`backend-v2`'s `getUserPermissionForBoard`
 *   returning nothing). Unlike `failed`, this is not a transient condition a
 *   retry can heal — the board stays denied until someone grants access — so
 *   it carries the server's own denial message and renders WITHOUT a retry
 *   affordance, instead of the generic "please try again" that invited the
 *   retry storm SE-7918 measured (17 identical denied requests in 15
 *   minutes).
 * - `stale` — the read errored but cached cards are still on hand.
 *   `errorPolicy: "all"` (see `providers/ApolloClient.tsx`) hands back
 *   cached data ALONGSIDE the error instead of rejecting, so the board still
 *   renders — flagged as possibly out of date — rather than discarding rows
 *   the user already had.
 * - `ready` — the read succeeded and returned cards.
 *
 * Consumers must switch exhaustively (with a `never` fallthrough) so a state
 * added later fails to compile rather than silently rendering as `empty`.
 */
export type KanbanCardsReadState =
  | { readonly kind: "loading" }
  | { readonly kind: "empty" }
  | { readonly kind: "failed" }
  | { readonly kind: "denied"; readonly message: string }
  | { readonly kind: "stale"; readonly cards: readonly KanbanCard[] }
  | { readonly kind: "ready"; readonly cards: readonly KanbanCard[] };

/** Inputs to {@link deriveKanbanCardsState}. */
interface KanbanCardsStateInput {
  /** Cards already extracted from the query result (may be empty). */
  readonly cards: readonly KanbanCard[];
  /**
   * Whether an answer is still outstanding — the query is skipped (no board
   * id yet) or genuinely in flight. Both cases mean an absent result is not
   * evidence of zero cards.
   */
  readonly isPending: boolean;
  /** Whether the hook surfaced a GraphQL or network error. */
  readonly hasError: boolean;
  /**
   * SE-8534: the backend's own denial message
   * ({@link resolveKanbanAccessDenial}) when `hasError` is a permission
   * refusal rather than a transient failure, else null/undefined. Takes
   * precedence over the generic `failed` state so the surface can render the
   * real reason and drop the retry affordance a denial can never satisfy.
   */
  readonly accessDeniedMessage?: string | null;
}

/**
 * Maps a Kanban cards query result onto its resolution state.
 *
 * Precedence, and why:
 * 1. In-flight wins outright — a retry (of a genuine transient failure) in
 *    progress shows progress rather than pinning a carried-over error or
 *    denial on screen.
 * 2. A confirmed permission denial (SE-8534) wins next, EVEN over cards
 *    already in hand — access has been authoritatively revoked, so
 *    continuing to render cached rows behind a "stale, tap to retry" banner
 *    would just re-issue the same request that can never succeed.
 * 3. Cards in hand otherwise win — a user with rows on screen is shown those
 *    rows. A non-denial error alongside them downgrades `ready` to `stale` so
 *    the staleness is disclosed rather than swallowed.
 * 4. A non-denial error with nothing to show is `failed` — never `empty`.
 * 5. Only a settled, error-free, empty read may claim `empty`.
 * @param input - Cards, pending flag, error flag, and denial message from the
 * hook.
 * @param input.cards - Cards already extracted from the query result.
 * @param input.isPending - Whether an answer is still outstanding.
 * @param input.hasError - Whether the hook surfaced an error.
 * @param input.accessDeniedMessage - The backend's denial message when the
 * error is a permission refusal, else null/undefined.
 * @returns The single state the board should render.
 */
export const deriveKanbanCardsState = ({
  cards,
  isPending,
  hasError,
  accessDeniedMessage,
}: KanbanCardsStateInput): KanbanCardsReadState => {
  if (isPending) {
    return { kind: "loading" };
  }
  if (accessDeniedMessage) {
    return { kind: "denied", message: accessDeniedMessage };
  }
  if (cards.length > 0) {
    return hasError ? { kind: "stale", cards } : { kind: "ready", cards };
  }
  if (hasError) {
    return { kind: "failed" };
  }
  return { kind: "empty" };
};

/**
 * How the board surface should treat a cards read state.
 *
 * Collapses the six-member {@link KanbanCardsReadState} down to the four
 * visual treatments the surface actually varies: render the board's normal
 * content (`loading`/`empty`/`ready` — loading and emptiness are handled by
 * the existing skeleton and column rendering), block on a full failure
 * screen with a retry affordance (`failed`), render the board with a
 * staleness disclosure (`stale`), or block on a permission-denied screen
 * with NO retry affordance (`denied`, SE-8534) — retrying a denial cannot
 * succeed, so unlike `failed` this treatment carries the server's own
 * message and nothing the surface can wire a retry button to.
 */
export type KanbanCardsUiTreatment =
  | { readonly kind: "content" }
  | { readonly kind: "failed" }
  | { readonly kind: "stale" }
  | { readonly kind: "denied"; readonly message: string };

/**
 * Resolves a cards read state to its UI treatment.
 *
 * Exhaustive by construction: the `never` fallthrough means a state added to
 * `KanbanCardsReadState` later fails to compile here instead of silently
 * falling back to the normal-content rendering — the failure mode this
 * ticket exists to remove.
 * @param state - The cards read's resolution state.
 * @returns The treatment the board surface should apply.
 */
export const resolveKanbanCardsUiTreatment = (
  state: KanbanCardsReadState
): KanbanCardsUiTreatment => {
  switch (state.kind) {
    case "loading":
    case "empty":
    case "ready":
      return { kind: "content" };
    case "failed":
      return { kind: "failed" };
    case "stale":
      return { kind: "stale" };
    case "denied":
      return { kind: "denied", message: state.message };
    default: {
      const exhaustive: never = state;
      return exhaustive;
    }
  }
};
